<template>
  <!-- ICONIC: Rollen in Klartext, eigene Rollen wie «Moderator» bauen -->
  <v-container class="rollen">
    <div class="d-flex flex-wrap align-end mb-4">
      <div class="mr-auto mb-2">
        <h1 class="text-h5 font-weight-medium">Rollen</h1>
        <div class="text-body-2 text--secondary">
          Was jede Rolle darf. Eigene Rollen baust du mit Häkchen statt Zahlen.
        </div>
      </div>
      <v-btn color="primary" depressed class="mb-2" @click="openEditor()">
        <v-icon left>mdi-plus</v-icon>Neue Rolle
      </v-btn>
    </div>

    <v-progress-linear v-if="loading" indeterminate class="mb-4"></v-progress-linear>

    <div class="text-overline mb-1">Rollen auf dem ganzen Server</div>
    <v-row dense class="mb-4">
      <v-col v-for="r in serverList" :key="r.sgid" cols="12" md="6">
        <v-card outlined class="fill-height d-flex flex-column">
          <v-card-title class="text-subtitle-1 font-weight-medium pb-1">
            {{ r.label }}
            <v-icon v-if="r.locked" small color="warning" class="ml-1">mdi-lock</v-icon>
            <v-spacer></v-spacer>
            <span class="text-caption text--secondary">{{ r.membersText }}</span>
          </v-card-title>
          <v-card-text class="pb-2 flex-grow-1">
            <div v-if="r.text" class="text-body-2">{{ r.text }}</div>
            <ul v-else-if="r.caps.length" class="caps text-body-2">
              <li v-for="c in r.caps" :key="c">{{ c }}</li>
            </ul>
            <div v-else class="text-body-2">Wie Normal, ohne Zusatzrechte.</div>
          </v-card-text>
          <v-card-actions v-if="r.custom" class="pt-0">
            <v-spacer></v-spacer>
            <v-btn text small @click="openEditor(r)">Bearbeiten</v-btn>
            <v-btn text small color="error" @click="askDelete(r)">Löschen</v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <div class="text-overline mb-1">Rollen in einzelnen Kanälen</div>
    <v-row dense>
      <v-col v-for="r in channelList" :key="r.key" cols="12" md="6">
        <v-card outlined class="fill-height">
          <v-card-title class="text-subtitle-1 font-weight-medium pb-1">
            {{ r.label }}
            <v-spacer></v-spacer>
            <span class="text-caption text--secondary">{{ r.membersText }}</span>
          </v-card-title>
          <v-card-text class="text-body-2">{{ r.text }}</v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <p class="text-caption text--secondary mt-3">
      Kanal-Rollen vergibst du bei der Person («Benutzer & Rechte») oder am Kanal («Kanäle & Leute»).
    </p>

    <!-- Rolle bauen / bearbeiten -->
    <v-dialog v-model="editor.open" max-width="600" scrollable>
      <v-card>
        <v-card-title>{{ editor.sgid ? `Rolle «${editor.original}» bearbeiten` : "Neue Rolle" }}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="editor.name"
            label="Name der Rolle (z. B. Moderator)"
            outlined
            dense
            counter="30"
            maxlength="30"
            autofocus
          ></v-text-field>
          <div class="text-body-2 mb-2">
            Wer diese Rolle hat, darf zusätzlich:
          </div>
          <v-checkbox
            v-for="cap in capabilities"
            :key="cap.key"
            v-model="editor.caps[cap.key]"
            :label="cap.label"
            dense
            hide-details
            class="mt-1"
          ></v-checkbox>
          <v-alert type="info" outlined dense class="mt-4 mb-0 text-body-2">
            Alles wirkt nur bei Normalen und Gästen. Server Admins und SuperAdmin
            sind zu stark dafür. Vergeben kannst du die Rolle bei der Person
            unter «Zusätzliche Rollen».
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="editor.open = false">Abbrechen</v-btn>
          <v-btn color="primary" depressed :loading="busy" :disabled="!String(editor.name || '').trim()" @click="saveRole">Speichern</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="del.open" max-width="440">
      <v-card>
        <v-card-title>Rolle «{{ del.name }}» löschen?</v-card-title>
        <v-card-text class="text-body-1">
          {{ del.members ? `${del.members} Person(en) verlieren damit diese Rolle.` : "Niemand hat diese Rolle." }}
          Das lässt sich nicht rückgängig machen.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="del.open = false">Abbrechen</v-btn>
          <v-btn color="error" depressed :loading="busy" @click="deleteRole">Löschen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { ensureServer, asList, resolveGroups, errorText } from "@/iconic/rechte";
import { CAPABILITIES, ROLE_BASE_PERMS, capabilitiesOf, BUILTIN_TEXT } from "@/iconic/rollen";

export default {
  data() {
    return {
      loading: true,
      busy: false,
      capabilities: CAPABILITIES,
      groups: { serverRoles: [], channelRoles: [], extraRoles: [] },
      members: {},
      channelMembers: {},
      capsBySg: {},
      normalPerms: {},
      editor: { open: false, sgid: null, name: "", original: "", caps: {}, before: {} },
      del: { open: false, sgid: null, name: "", members: 0 },
    };
  },
  computed: {
    serverList() {
      const count = (sgid, fallback) => {
        const n = this.members[sgid];
        if (n === undefined) return fallback || "";
        return n === 1 ? "1 Person" : `${n} Personen`;
      };
      const builtin = this.groups.serverRoles.map((r) => ({
        sgid: r.sgid,
        label: r.label,
        text: BUILTIN_TEXT[r.key],
        caps: [],
        custom: false,
        locked: false,
        membersText: r.key === "guest" ? "alle anderen" : count(r.sgid),
      }));
      const extra = this.groups.extraRoles.map((r) => ({
        sgid: r.sgid,
        label: r.name,
        text: r.locked ? BUILTIN_TEXT[r.name] || "Geschützt." : "",
        caps: r.locked ? [] : CAPABILITIES.filter((c) => (this.capsBySg[r.sgid] || {})[c.key]).map((c) => c.label),
        custom: !r.locked,
        locked: r.locked,
        membersText: count(r.sgid),
      }));
      return builtin.concat(extra);
    },
    channelList() {
      return this.groups.channelRoles.map((r) => {
        const n = r.cgid ? this.channelMembers[r.cgid] || 0 : 0;
        return {
          key: r.key,
          label: r.label,
          text: r.text,
          membersText: !r.cgid ? "noch nicht angelegt" : n === 1 ? "1 Zuordnung" : `${n} Zuordnungen`,
        };
      });
    },
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        await ensureServer();
        const ts = this.$TeamSpeak;
        const [serverInfo, serverGroups, channelGroups, assignments] = await Promise.all([
          ts.execute("serverinfo").then((r) => asList(r)[0]),
          ts.execute("servergrouplist").then(asList),
          ts.execute("channelgrouplist").then(asList),
          ts.execute("channelgroupclientlist").then(asList),
        ]);
        const groups = resolveGroups(serverInfo, serverGroups, channelGroups);
        const members = {};
        const ids = groups.serverRoles.filter((r) => r.key !== "guest").map((r) => r.sgid).concat(groups.extraRoles.map((r) => r.sgid));
        for (const sgid of ids) {
          members[sgid] = asList(await ts.execute("servergroupclientlist", { sgid })).length;
        }
        const caps = {};
        for (const r of groups.extraRoles.filter((x) => !x.locked)) {
          caps[r.sgid] = capabilitiesOf(asList(await ts.execute("servergrouppermlist", { sgid: r.sgid }, ["-permsid"])));
        }
        const normal = groups.serverRoles.find((r) => r.key === "normal");
        const normalPerms = {};
        if (normal) {
          asList(await ts.execute("servergrouppermlist", { sgid: normal.sgid }, ["-permsid"])).forEach(
            (p) => (normalPerms[p.permsid] = { value: Number(p.permvalue), skip: p.permskip ? 1 : 0 })
          );
        }
        const channelMembers = {};
        assignments.forEach((a) => (channelMembers[String(a.cgid)] = (channelMembers[String(a.cgid)] || 0) + 1));
        Object.assign(this, { groups, members, capsBySg: caps, normalPerms, channelMembers });
      } catch (err) {
        this.$toast.error(errorText(err));
      }
      this.loading = false;
    },
    openEditor(role) {
      const caps = {};
      CAPABILITIES.forEach((c) => (caps[c.key] = role ? Boolean((this.capsBySg[role.sgid] || {})[c.key]) : ["kickChannel", "move", "protect"].includes(c.key)));
      this.editor = {
        open: true,
        sgid: role ? role.sgid : null,
        name: role ? role.label : "",
        original: role ? role.label : "",
        caps,
        before: role ? { ...(this.capsBySg[role.sgid] || {}) } : {},
      };
    },
    async saveRole() {
      const ts = this.$TeamSpeak;
      const { sgid: existing, caps, before } = this.editor;
      const name = String(this.editor.name).trim();
      this.busy = true;
      try {
        let sgid = existing;
        if (!sgid) {
          const normal = this.groups.serverRoles.find((r) => r.key === "normal");
          if (!normal) throw new Error("Die Rolle «Normal» fehlt als Vorlage.");
          sgid = String(asList(await ts.execute("servergroupcopy", { ssgid: normal.sgid, tsgid: 0, name, type: 1 }))[0].sgid);
          for (const [permsid, permvalue] of ROLE_BASE_PERMS)
            await ts.execute("servergroupaddperm", { sgid, permsid, permvalue, permnegated: 0, permskip: 0 });
        } else if (name !== this.editor.original) {
          await ts.execute("servergrouprename", { sgid, name });
        }
        for (const cap of CAPABILITIES) {
          const on = Boolean(caps[cap.key]);
          const was = Boolean(before[cap.key]);
          if (on && !was) {
            for (const [permsid, permvalue] of cap.perms)
              await ts.execute("servergroupaddperm", { sgid, permsid, permvalue, permnegated: 0, permskip: 1 });
          } else if (!on && was) {
            // zurück auf den Stand von «Normal» (die Vorlage), sonst entfernen
            for (const [permsid] of cap.perms) {
              const n = this.normalPerms[permsid];
              if (n) await ts.execute("servergroupaddperm", { sgid, permsid, permvalue: n.value, permnegated: 0, permskip: n.skip });
              else await ts.execute("servergroupdelperm", { sgid, permsid });
            }
          }
        }
        this.$toast.success(existing ? "Rolle gespeichert." : `Rolle «${name}» angelegt.`);
        this.editor.open = false;
      } catch (err) {
        this.$toast.error(errorText(err));
      }
      this.busy = false;
      await this.load();
    },
    askDelete(role) {
      this.del = { open: true, sgid: role.sgid, name: role.label, members: this.members[role.sgid] || 0 };
    },
    async deleteRole() {
      this.busy = true;
      try {
        await this.$TeamSpeak.execute("servergroupdel", { sgid: this.del.sgid, force: 1 });
        this.$toast.success(`Rolle «${this.del.name}» gelöscht.`);
        this.del.open = false;
      } catch (err) {
        this.$toast.error(errorText(err));
      }
      this.busy = false;
      await this.load();
    },
  },
  created() {
    this.load();
  },
};
</script>

<style scoped>
.rollen {
  max-width: 1000px;
}
.caps {
  padding-left: 1.1em;
}
</style>
